/**
 * NavigationHistory Component
 *
 * This component implements the breadcrumb navigation system for the Trip Planner application.
 * It dynamically generates navigation breadcrumbs based on the current route and application state,
 * providing hierarchical context and navigation shortcuts to users.
 *
 * The breadcrumbs adjust based on:
 * 1. Current location in the app (e.g., trips list, specific trip, places, travel, accommodation)
 * 2. Whether a specific item is being viewed (a place, travel arrangement, or accommodation)
 * 3. User's authentication state
 *
 * Phase 1.3 Enhancement:
 * - Integrated ChatToggle button for discussion overlay
 * - Appears in breadcrumb bar when viewing a trip
 */
import { useEffect, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useTrip, formatDate , useAuth } from '@trip-planner-monorepo/shared';

import { placesService } from '@trip-planner-monorepo/places';
import { travelService } from '@trip-planner-monorepo/travel';
import { accommodationService } from '@trip-planner-monorepo/accommodation';
import { daysService } from '@trip-planner-monorepo/days';
import { documentService } from '@trip-planner-monorepo/documents';
import { ChatToggle, DiscussionOverlay } from '@trip-planner-monorepo/app-features';
import { FaHome, FaChevronRight } from 'react-icons/fa';
import './NavigationHistory.css';

const NavigationHistory = () => {
  /**
   * React Router hooks for navigation and routing
   * - location: Current URL and path information
   * - navigate: Function to programmatically navigate to different routes
   * - Route parameters extracted from the URL
   */
  const location = useLocation();
  const navigate = useNavigate();
  
  /**
   * Context hooks for application state
   * - Trip context provides information about the currently selected trip
   * - Auth context provides the current user for data fetching operations
   */
  const { currentTrip, updateTripLocation, effectiveUserId } = useTrip();
  const { currentUser } = useAuth();
  
  /**
   * URL parameters extracted from the current route
   * These IDs are used to fetch specific data and determine the current location
   */
  const { tripId, placeId, travelId, accommodationId, dayId, documentId } = useParams();
  
  /**
   * Local state for storing details of specific items
   * These details are used to display meaningful names in the breadcrumbs
   */
  const [placeDetails, setPlaceDetails] = useState(null);
  const [travelDetails, setTravelDetails] = useState(null);
  const [accommodationDetails, setAccommodationDetails] = useState(null);
  const [dayBreadcrumbDetails, setDayBreadcrumbDetails] = useState(null);
  const [documentDetails, setDocumentDetails] = useState(null);

  /**
   * Discussion overlay state
   * Phase 1.3: Manages visibility of discussion overlay
   */
  const [discussionOpen, setDiscussionOpen] = useState(false);

  /**
   * Effect: Load Place Details
   * 
   * PURPOSE:
   * Fetches details of the currently viewed place to display its name in the breadcrumbs
   * 
   * PROCESS:
   * 1. Runs when placeId, tripId, or currentUser changes
   * 2. Fetches all places for the current trip
   * 3. Finds the specific place by ID
   * 4. Updates the placeDetails state
   * 5. Clears the state if placeId is not present
   * 
   * ERROR HANDLING:
   * Logs errors to the console but doesn't disrupt the UI
   */
  useEffect(() => {
    const loadPlaceDetails = async () => {
      if (currentUser && tripId && placeId) {
        try {
          const places = await placesService.fetchPlaces(effectiveUserId, tripId);
          const place = places.find(p => p.id === placeId);
          if (place) {
            setPlaceDetails(place);
          }
        } catch (error) {
          console.error('Error loading place details for breadcrumb:', error);
          setPlaceDetails(null);
        }
      }
    };

    if (placeId && tripId && currentUser) {
      loadPlaceDetails();
    } else {
      setPlaceDetails(null);
    }
  }, [currentUser, tripId, placeId, effectiveUserId]);

  /**
   * Effect: Load Travel Details
   * 
   * PURPOSE:
   * Fetches details of the currently viewed travel arrangement to display its type in the breadcrumbs
   * 
   * PROCESS:
   * Same pattern as place details loading, but for travel items
   */
  useEffect(() => {
    const loadTravelDetails = async () => {
      if (currentUser && tripId && travelId) {
        try {
          const travels = await travelService.fetchTravel(effectiveUserId, tripId);
          const travel = travels.find(t => t.id === travelId);
          if (travel) {
            setTravelDetails(travel);
          }
        } catch (error) {
          console.error('Error loading travel details for breadcrumb:', error);
          setTravelDetails(null);
        }
      }
    };

    if (travelId && tripId && currentUser) {
      loadTravelDetails();
    } else {
      setTravelDetails(null);
    }
  }, [currentUser, tripId, travelId, effectiveUserId]);

  /**
   * Effect: Load Accommodation Details
   * 
   * PURPOSE:
   * Fetches details of the currently viewed accommodation to display its name/type in the breadcrumbs
   * 
   * PROCESS:
   * Same pattern as place and travel details loading, but for accommodation items
   */
  useEffect(() => {
    const loadAccommodationDetails = async () => {
      if (currentUser && tripId && accommodationId) {
        try {
          const accommodations = await accommodationService.fetchAccommodation(effectiveUserId, tripId);
          const accommodation = accommodations.find(a => a.id === accommodationId);
          if (accommodation) {
            setAccommodationDetails(accommodation);
          }
        } catch (error) {
          console.error('Error loading accommodation details for breadcrumb:', error);
          setAccommodationDetails(null);
        }
      }
    };

    if (accommodationId && tripId && currentUser) {
      loadAccommodationDetails();
    } else {
      setAccommodationDetails(null);
    }
  }, [currentUser, tripId, accommodationId, effectiveUserId]);

  /**
   * Effect: Load Day Details for Breadcrumb
   * 
   * PURPOSE:
   * Fetches details of the currently viewed day to display its number and date in the breadcrumbs.
   */
  useEffect(() => {
    const loadDayBreadcrumbDetails = async () => {
      if (currentUser && tripId && dayId) {
        try {
          const allDays = await daysService.fetchDays(effectiveUserId, tripId);
          const currentDay = allDays.find(d => d.id === dayId);
          if (currentDay) {
            const dayIndex = allDays.sort((a, b) => a.order - b.order).findIndex(d => d.id === dayId);
            setDayBreadcrumbDetails({
              dayNumber: dayIndex + 1,
              date: currentDay.date,
            });
          } else {
            setDayBreadcrumbDetails(null);
          }
        } catch (error) {
          console.error('Error loading day details for breadcrumb:', error);
          setDayBreadcrumbDetails(null);
        }
      }
    };

    if (dayId && tripId && currentUser) {
      loadDayBreadcrumbDetails();
    } else {
      setDayBreadcrumbDetails(null);
    }
  }, [currentUser, tripId, dayId, effectiveUserId]);

  /**
   * Effect: Load Document Details
   * 
   * PURPOSE:
   * Fetches details of the currently viewed document to display its title in the breadcrumbs.
   */
  useEffect(() => {
    const loadDocumentDetails = async () => {
      if (currentUser && tripId && documentId) {
        try {
          const document = await documentService.getDocument(effectiveUserId, tripId, documentId);
          if (document) {
            setDocumentDetails({
              title: document.title || document.name || 'Untitled Document',
              name: document.name,
              type: document.primaryCategory || 'document'
            });
          } else {
            setDocumentDetails(null);
          }
        } catch (error) {
          console.error('Error loading document details for breadcrumb:', error);
          setDocumentDetails(null);
        }
      }
    };

    if (documentId && tripId && currentUser) {
      loadDocumentDetails();
    } else {
      setDocumentDetails(null);
    }
  }, [currentUser, tripId, documentId, effectiveUserId]);

  /**
   * Effect: Update Trip Location
   * 
   * PURPOSE:
   * Tracks the user's last visited location within a trip for returning users
   * 
   * PROCESS:
   * When the path changes, updates the trip's last visited location in the context
   * This allows the app to restore the user's position when they return to a trip
   */
  useEffect(() => {
    if (tripId) {
      updateTripLocation(tripId, location.pathname);
    }
  }, [tripId, location.pathname, updateTripLocation]);

  /**
   * Home Navigation Handler
   * 
   * PURPOSE:
   * Provides a special navigation behavior for the home breadcrumb
   * 
   * HOW IT WORKS:
   * 1. Prevents the default behavior of the button
   * 2. Uses replace:true to avoid adding a new history entry
   * 3. Preserves the trip selection state when navigating to home
   */
  const handleHomeClick = (e) => {
    e.preventDefault();
    navigate('/', { replace: true });
  };

  /**
   * Breadcrumb Generation Function
   * 
   * PURPOSE:
   * Core function that dynamically generates breadcrumbs based on the current route and app state
   * 
   * ALGORITHM:
   * 1. Parse the current URL path into segments
   * 2. Always start with a home (Trips) breadcrumb
   * 3. Add trip-specific breadcrumbs if viewing a trip
   * 4. Add section breadcrumbs (places, travel, accommodation)
   * 5. Add detail breadcrumbs for specific items
   * 
   * RETURNS:
   * An array of breadcrumb objects, each with:
   * - name: Display text
   * - path: Target URL
   * - Special flags (isHome, isTrip, isCurrent)
   */
  const getBreadcrumbs = () => {
    /**
     * Breadcrumb Generation Logic:
     * 
     * This function dynamically constructs the breadcrumb trail based on the current URL path 
     * and, importantly, any pre-existing breadcrumb trail passed via navigation state 
     * (location.state.previousBreadcrumbs).
     *
     * Original Behavior (when no previousBreadcrumbs are passed):
     * 1. Parses the current URL (location.pathname) into segments.
     * 2. Always starts with a "Trips" (home) breadcrumb.
     * 3. Identifies the current trip and section (e.g., Days, Places, Accommodation) from the URL.
     * 4. Fetches details for specific entities (trip name, place name, accommodation summary, etc.) 
     *    using IDs from the URL parameters (tripId, placeId, etc.) to make breadcrumb labels more descriptive.
     *    (See useEffect hooks for loadPlaceDetails, loadAccommodationDetails etc.)
     * 5. Constructs breadcrumbs for each relevant segment of the path.
     *
     * Enhanced Behavior (when location.state.previousBreadcrumbs IS present):
     * This enhancement was added to support scenarios where navigation to a page (e.g., a Place detail page)
     * should append to an existing breadcrumb trail from a different context (e.g., an Accommodation detail page).
     * 
     * 1. If `location.state.previousBreadcrumbs` is found:
     *    a. This array is used as the initial base for the breadcrumbs.
     *    b. All items from `previousBreadcrumbs` are explicitly marked as `isCurrent: false` because 
     *       they represent the path taken to get to the *previous* page.
     * 2. The function then appends new breadcrumb segments relevant to the *current* page.
     *    For instance, if navigating from an Accommodation to a Place:
     *        - `previousBreadcrumbs` might be: Trips > TripName > Accommodation > HotelName
     *        - This function will append: > Places > PlaceName
     * 3. The `section` variable is determined from the current URL to correctly identify what new segments 
     *    to append (e.g., 'places' when on a place detail page).
     * 4. Data fetching (e.g., `placeDetails` for `placeDetails.name`) is still crucial for the names 
     *    of these newly appended segments.
     * 5. Finally, a loop ensures that only the very last item in the fully combined breadcrumb trail 
     *    is marked as `isCurrent: true`.
     *
     * Path & State Management:
     * - `location.pathname`: Source of truth for the current URL.
     * - `location.state.previousBreadcrumbs`: How a navigating component can pass its context.
     * - `useParams()`: Provides dynamic parts of the URL (tripId, placeId, etc.).
     * - `currentTrip`, `placeDetails`, `travelDetails`, `accommodationDetails`, `dayBreadcrumbDetails`: State variables 
     *   holding fetched data to enrich breadcrumb labels.
     */
    const pathSegments = location.pathname.split('/').filter(Boolean);
    let breadcrumbs = [];

    // Check for previous breadcrumbs passed in navigation state
    if (location.state?.previousBreadcrumbs) {
      // Ensure previous breadcrumbs are not marked as current
      breadcrumbs = location.state.previousBreadcrumbs.map(crumb => ({ ...crumb, isCurrent: false }));
    } else {
      // Default starting breadcrumb if no previous ones are passed
      breadcrumbs.push({ name: 'Trips', path: '/', isHome: true, isCurrent: pathSegments.length === 0 });
    }

    if (currentTrip && tripId) {
      let section = 'days';
      if (pathSegments.includes('day') && dayId) section = 'day';
      else if (pathSegments.includes('places')) section = 'places';
      else if (pathSegments.includes('travel')) section = 'travel';
      else if (pathSegments.includes('accommodation')) section = 'accommodation';
      else if (pathSegments.includes('documents')) section = 'documents';
      else if (pathSegments.includes('extras')) section = 'extras';

      if (pathSegments.length > 1 && !location.state?.previousBreadcrumbs) {
        breadcrumbs.push({
          name: 'Days',
          path: `/trips/${tripId}`,
          isTrip: true,
          isCurrent: section === 'days' && pathSegments.length === 2 && !dayId && !placeId && !travelId && !accommodationId && !documentId
        });
      }
      
      // If we have previous breadcrumbs, the section determination for "Places" is specific to the current context
      if (location.state?.previousBreadcrumbs && section === 'places') {
        // Add the "Places" segment
        breadcrumbs.push({
          name: 'Places',
          path: `/trips/${tripId}/places`, // This path might need adjustment depending on your exact routing for nested "Places"
          isCurrent: !placeId 
        });
        // If there's a placeId, add the specific place
        if (placeId && placeDetails) {
          breadcrumbs.push({
            name: placeDetails.name,
            path: `/trips/${tripId}/places/${placeId}`,
            isCurrent: true
          });
        }
      } else if (section !== 'days' && section !== 'day' && !location.state?.previousBreadcrumbs) {
        breadcrumbs.push({
          name: section.charAt(0).toUpperCase() + section.slice(1),
          path: `/trips/${tripId}/${section}`,
          isCurrent: pathSegments.includes(section) && pathSegments.length === 3 && !placeId && !travelId && !accommodationId && !documentId
        });
      }

      // Original logic for specific sections if not handling a "Places" continuation
      if (!location.state?.previousBreadcrumbs || (location.state?.previousBreadcrumbs && section !== 'places')) {
        if (section === 'places' && placeId && placeDetails) {
          breadcrumbs.push({
            name: placeDetails.name,
            path: `/trips/${tripId}/places/${placeId}`,
            isCurrent: true
          });
        }

        if (section === 'travel' && travelId && travelDetails && travelDetails.type) {
          const travelTypeName = travelDetails.type.charAt(0).toUpperCase() + travelDetails.type.slice(1);
          breadcrumbs.push({
            name: travelTypeName,
            path: `/trips/${tripId}/travel/${travelId}`,
            isCurrent: true
          });
        }

        if (section === 'accommodation' && accommodationId && accommodationDetails) {
          const accommodationName = accommodationDetails.summary || 
            (accommodationDetails.type ? 
              accommodationDetails.type.charAt(0).toUpperCase() + accommodationDetails.type.slice(1) : 
              'Accommodation');
          breadcrumbs.push({
            name: accommodationName,
            path: `/trips/${tripId}/accommodation/${accommodationId}`,
            isCurrent: true
          });
        }
        
        if (section === 'day' && dayId && dayBreadcrumbDetails && dayBreadcrumbDetails.date) {
          breadcrumbs.push({
            name: `Day ${dayBreadcrumbDetails.dayNumber}: ${formatDate(dayBreadcrumbDetails.date)}`,
            path: location.pathname,
            isCurrent: true
          });
        }

        if (section === 'documents' && documentId && documentDetails) {
          breadcrumbs.push({
            name: documentDetails.title,
            path: `/trips/${tripId}/documents/${documentId}`,
            isCurrent: true
          });
        }
      }
    }
    
    // If breadcrumbs were passed, ensure only the last one is current
    if (location.state?.previousBreadcrumbs && breadcrumbs.length > 0) {
      for (let i = 0; i < breadcrumbs.length -1; i++) {
        breadcrumbs[i].isCurrent = false;
      }
      breadcrumbs[breadcrumbs.length - 1].isCurrent = true;
    }

    return breadcrumbs;
  };

  const breadcrumbs = getBreadcrumbs();

  /**
   * Component Rendering
   * 
   * STRUCTURE:
   * - <nav> container with ARIA label for accessibility
   * - <ol> list of breadcrumb items
   * - Conditional rendering of separators (chevron icons) between items
   * - Different rendering for current (non-clickable) vs. clickable breadcrumbs
   * - Special handling for home breadcrumb with home icon
   */
  return (
    <>
      <nav className="breadcrumb-navigation" aria-label="Breadcrumb">
        <ol className="breadcrumb-list">
          {breadcrumbs.map((crumb, index) => (
            <li
              key={crumb.path + '-' + index}
              className={`breadcrumb-item ${crumb.isCurrent ? 'current' : ''}`}
            >
              {index > 0 && (
                <FaChevronRight className="breadcrumb-separator" aria-hidden="true" />
              )}

              {crumb.isCurrent ? (
                <span
                  className="breadcrumb-text current"
                  title={crumb.name}
                >
                  {crumb.isHome ? (
                    <>
                      <FaHome className="breadcrumb-icon" aria-hidden="true" />
                      <span className="breadcrumb-label">{crumb.name}</span>
                    </>
                  ) : (
                    crumb.name
                  )}
                </span>
              ) : (
                <>
                  {crumb.isHome ? (
                    <button
                      onClick={handleHomeClick}
                      className="breadcrumb-link home-link"
                      title={crumb.name}
                    >
                      <FaHome className="breadcrumb-icon" aria-hidden="true" />
                      <span className="breadcrumb-label">{crumb.name}</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => navigate(crumb.path)}
                      className="breadcrumb-link"
                      title={crumb.name}
                    >
                      {crumb.name}
                    </button>
                  )}
                </>
              )}
            </li>
          ))}

          {/* Discussion Toggle Button - Phase 1.3 */}
          {currentTrip && (
            <li className="breadcrumb-item chat-toggle-item">
              <ChatToggle
                onClick={() => setDiscussionOpen(!discussionOpen)}
                isOpen={discussionOpen}
                label="Discuss"
              />
            </li>
          )}
        </ol>
      </nav>

      {/* Discussion Overlay - Phase 1.3 */}
      {currentTrip && (
        <DiscussionOverlay
          isOpen={discussionOpen}
          onClose={() => setDiscussionOpen(false)}
          tripId={currentTrip.id}
        />
      )}
    </>
  );
};

export default NavigationHistory;